import React, { useEffect, useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import api, { fotoUrl } from '../api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

interface Sinyal {
  tip: 'yoklama' | 'hak' | 'katilim' | 'odeme' | 'rsvp';
  puan: number;
  mesaj: string;
}

interface RiskliOgrenci {
  id: string;
  ad: string;
  soyad: string;
  email: string;
  foto_url: string | null;
  sube_adi: string | null;
  skor: number;
  seviye: 'yuksek' | 'orta' | 'dusuk';
  sinyaller: Sinyal[];
  son_yoklama_tarihi: string | null;
  son_yoklama_ders: string | null;
  son_yoklama_gun: number | null;
  kalan_hak: number;
  aktif_ders_sayisi: number;
}

interface Ozet {
  toplam: number;
  yuksek: number;
  orta: number;
  dusuk: number;
}

const SINYAL_ETIKET: Record<string, string> = {
  yoklama: 'Son giriş',
  hak: 'Ders hakkı',
  katilim: 'Katılım',
  odeme: 'Ödeme',
  rsvp: 'RSVP',
};

function RiskBadge({ seviye, skor }: { seviye: string; skor: number }) {
  const { isDark } = useTheme();
  const renk =
    seviye === 'yuksek'
      ? isDark
        ? { bg: 'rgba(239,68,68,0.15)', color: '#FCA5A5', border: 'rgba(239,68,68,0.3)' }
        : { bg: '#FEE2E2', color: '#B91C1C', border: '#FECACA' }
      : seviye === 'orta'
      ? isDark
        ? { bg: 'rgba(245,158,11,0.15)', color: '#FCD34D', border: 'rgba(245,158,11,0.3)' }
        : { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' }
      : isDark
      ? { bg: 'rgba(16,185,129,0.15)', color: '#6EE7B7', border: 'rgba(16,185,129,0.3)' }
      : { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' };
  const etiket =
    seviye === 'yuksek' ? 'Yüksek risk' :
    seviye === 'orta'   ? 'Orta risk' : 'Düşük risk';
  return (
    <span style={{
      background: renk.bg, color: renk.color, border: `1px solid ${renk.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {etiket} ({skor})
    </span>
  );
}

function SinyalBar({ sinyaller }: { sinyaller: Sinyal[] }) {
  const { isDark } = useTheme();
  const textMuted = isDark ? '#666' : '#aaa';
  const textSec   = isDark ? '#999' : '#666';
  const barBg     = isDark ? '#333' : '#f0f0f0';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {sinyaller.filter(s => s.puan > 0).map(s => (
        <div key={s.tip} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
          <span style={{ color: textMuted, minWidth: 60 }}>{SINYAL_ETIKET[s.tip]}</span>
          <div style={{ flex: 1, background: barBg, borderRadius: 4, height: 6, minWidth: 60, maxWidth: 100 }}>
            <div style={{
              width: `${(s.puan / 30) * 100}%`, maxWidth: '100%',
              height: '100%', borderRadius: 4,
              background: s.puan >= 20 ? '#EF4444' : s.puan >= 10 ? '#F59E0B' : '#10B981'
            }} />
          </div>
          <span style={{ color: textSec, minWidth: 120 }}>{s.mesaj}</span>
        </div>
      ))}
    </div>
  );
}

const RiskRaporu: React.FC = () => {
  usePageTitle('Risk Raporu');
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#161b22' : '#FAFAFA';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec   = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';

  const [veri, setVeri] = useState<{ ozet: Ozet; ogrenciler: RiskliOgrenci[] } | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [filtre, setFiltre] = useState<'tum' | 'yuksek' | 'orta' | 'dusuk'>('tum');
  const [arama, setArama] = useState('');
  const [acilanId, setAcilanId] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/ai/risk-ozeti')
      .then(res => setVeri(res.data))
      .catch(err => console.error(err))
      .finally(() => setYukleniyor(false));
  }, []);

  const liste = (veri?.ogrenciler || [])
    .filter(o => filtre === 'tum' || o.seviye === filtre)
    .filter(o => `${o.ad} ${o.soyad} ${o.email}`.toLowerCase().includes(arama.toLowerCase()));

  const filtreBtnler = [
    { key: 'tum',    label: 'Tümü',        sayi: veri?.ozet.toplam ?? 0, renk: '#534AB7', bgLight: '#EEEDFE', bgDark: 'rgba(83,74,183,0.2)' },
    { key: 'yuksek', label: 'Yüksek Risk', sayi: veri?.ozet.yuksek ?? 0, renk: '#B91C1C', bgLight: '#FEE2E2', bgDark: 'rgba(185,28,28,0.2)' },
    { key: 'orta',   label: 'Orta Risk',   sayi: veri?.ozet.orta   ?? 0, renk: '#92400E', bgLight: '#FEF3C7', bgDark: 'rgba(146,64,14,0.2)' },
    { key: 'dusuk',  label: 'Düşük Risk',  sayi: veri?.ozet.dusuk  ?? 0, renk: '#065F46', bgLight: '#D1FAE5', bgDark: 'rgba(6,95,70,0.2)' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, marginBottom: 4, color: textPrimary }}>Risk Raporu</h1>
        <p style={{ fontSize: 13, color: textSec, margin: 0 }}>
          Öğrenci bırakma riski — son giriş, katılım oranı, ders hakkı ve ödeme düzenine göre otomatik hesaplanır
        </p>
      </div>

      {!yukleniyor && veri && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {filtreBtnler.map(({ key, label, sayi, renk, bgLight, bgDark }) => (
            <button key={key}
              onClick={() => setFiltre(key as typeof filtre)}
              style={{
                padding: '10px 18px', borderRadius: 10, border: '1.5px solid',
                fontSize: 13, cursor: 'pointer', fontWeight: filtre === key ? 700 : 400,
                background: filtre === key ? (isDark ? bgDark : bgLight) : card,
                color: filtre === key ? renk : textSec,
                borderColor: filtre === key ? renk : (isDark ? '#333' : '#ddd'),
              }}>
              {label}
              <span style={{
                marginLeft: 8, borderRadius: 20, padding: '1px 8px', fontSize: 11,
                background: filtre === key ? renk : (isDark ? '#333' : '#eee'),
                color: filtre === key ? 'white' : textSec,
              }}>
                {sayi}
              </span>
            </button>
          ))}
        </div>
      )}

      <input
        value={arama}
        onChange={e => setArama(e.target.value)}
        placeholder="İsim veya email ara..."
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`,
          fontSize: 13, marginBottom: 14, boxSizing: 'border-box',
          background: card, color: textPrimary,
        }}
      />

      <div style={{ background: card, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
        {yukleniyor ? (
          <div style={{ padding: 40, textAlign: 'center', color: textSec, fontSize: 13 }}>Hesaplanıyor...</div>
        ) : liste.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: textSec, fontSize: 13 }}>Sonuç yok.</div>
        ) : (
          <>
            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${border}`, fontSize: 12, color: textSec }}>
              <span style={{ fontWeight: 600, color: '#534AB7' }}>{liste.length}</span> öğrenci
            </div>
            {liste.map((o, i) => (
              <div key={o.id}
                style={{ borderBottom: i < liste.length - 1 ? `1px solid ${borderSub}` : 'none' }}>
                {/* Ana satır */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12, cursor: 'pointer' }}
                  onClick={() => setAcilanId(acilanId === o.id ? null : o.id)}>
                  {/* Avatar */}
                  {o.foto_url
                    ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
                    : <div style={{ width: 36, height: 36, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                        {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                      </div>
                  }
                  {/* İsim */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13, color: '#534AB7' }}
                      onClick={e => { e.stopPropagation(); navigate(`/ogrenciler/${o.id}`); }}>
                      {o.ad} {o.soyad}
                    </div>
                    <div style={{ fontSize: 11, color: textMuted }}>{o.email}</div>
                  </div>
                  {/* Son yoklama */}
                  <div style={{ fontSize: 12, color: textSec, textAlign: 'right', minWidth: 100 }}>
                    {o.son_yoklama_tarihi
                      ? <>
                          <div>{new Date(o.son_yoklama_tarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}</div>
                          {o.son_yoklama_ders && <div style={{ fontSize: 10, color: textMuted }}>{o.son_yoklama_ders}</div>}
                        </>
                      : <span style={{ color: isDark ? '#444' : '#ccc' }}>Giriş yok</span>
                    }
                  </div>
                  {/* Kalan hak */}
                  <div style={{ fontSize: 12, color: textSec, minWidth: 60, textAlign: 'center' }}>
                    {o.kalan_hak > 0
                      ? <span style={{ color: '#065F46', background: isDark ? 'rgba(16,185,129,0.15)' : '#D1FAE5', borderRadius: 20, padding: '2px 8px' }}>{o.kalan_hak} hak</span>
                      : <span style={{ color: '#B91C1C', background: isDark ? 'rgba(185,28,28,0.15)' : '#FEE2E2', borderRadius: 20, padding: '2px 8px' }}>Hak yok</span>
                    }
                  </div>
                  {/* Risk badge */}
                  <RiskBadge seviye={o.seviye} skor={o.skor} />
                  {/* Genişlet oku */}
                  <span style={{ fontSize: 11, color: textMuted, marginLeft: 4 }}>{acilanId === o.id ? '▲' : '▼'}</span>
                </div>

                {/* Detay panel */}
                {acilanId === o.id && (
                  <div style={{ padding: '0 16px 14px 64px', background: cardAlt }}>
                    <SinyalBar sinyaller={o.sinyaller} />
                    {o.sinyaller.filter(s => s.puan > 0).length === 0 && (
                      <div style={{ fontSize: 12, color: textMuted }}>Tüm sinyaller normal — düşük risk</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default RiskRaporu;
